import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Site,
  Message,
  UserFeedbackSubmission,
  StreamEventType,
  StreamingStep,
  DocumentRef,
  ClarificationOption,
  SuggestedQuestionItem,
} from '../../types';
import { askQuestion, askQuestionStream, checkAnswerQuality, submitUserFeedback, fetchSuggestedQuestions } from '../../services/api';
import { STREAMING_ENABLED } from '../../config/config';
import { getErrorMessage } from '../../utils/errorUtils';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import {
  ChatButton,
  ChatPanel,
  ChatHeader,
  HeaderTitle,
  SiteName,
  SiteLabel,
  CloseButton,
  InlineChatContainer,
} from './ChatBox.styles';
import { Chat, Close } from '../icons/s2';

// Props for floating panel mode (original behavior)
interface FloatingChatBoxProps {
  site: Site | null;
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  inline?: false;
}

// Props for inline mode (full page chat view)
interface InlineChatBoxProps {
  site: Site;
  inline: true;
  initialMessages?: Message[];
  initialConversationId?: string | null;
  onConversationUpdate?: () => void;
  /** Fires once when the backend assigns an id to a brand-new chat, so the
   * caller can surface it (e.g. into the URL). Not fired on later turns of an
   * already-known conversation. */
  onConversationIdAssigned?: (conversationId: string) => void;
}

type ChatBoxProps = FloatingChatBoxProps | InlineChatBoxProps;

// Generate unique ID for messages (only for client-side use)
const generateId = () => `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

/**
 * Synthesise a reasoning string from the `complete` event payload when the
 * top-level `answer_reasoning` is empty.  Covers composite queries (pulls
 * per-sub-query reasoning from `sub_query_results`) and clarification
 * responses.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function resolveAnswerReasoning(data: Record<string, any>, t: TFunction): string | undefined {
  if (data.answer_reasoning) return data.answer_reasoning;

  // Clarification: the pipeline asked the user for more info
  if (data.clarification) {
    return t('chat.answer.clarificationReasoning');
  }

  // Composite: collect per-sub-query reasoning from sub_query_results
  const subResults: Record<string, any> | undefined = data.sub_query_results;
  if (subResults && typeof subResults === 'object') {
    const parts: string[] = [];
    for (const [sqId, result] of Object.entries(subResults)) {
      const r = (result as Record<string, any>)?.answer_reasoning;
      if (r) parts.push(t('chat.answer.subReasoning', { id: sqId, reasoning: r }));
    }
    if (parts.length > 0) return parts.join('\n');
  }

  return undefined;
}

function normalizeClarificationOptions(raw: unknown): ClarificationOption[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const out: ClarificationOption[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const title = String(o.title ?? o.label ?? '').trim();
    if (!title) continue;
    const idRaw = o.id != null && String(o.id).trim() ? String(o.id).trim() : title;
    const hintVal = o.hint;
    const hint = typeof hintVal === 'string' && hintVal.trim() ? hintVal.trim() : undefined;
    out.push({ id: idRaw, title, hint });
  }
  return out.length ? out : undefined;
}

function normalizeSuggestedQuestions(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const out = raw.map((s) => String(s).trim()).filter(Boolean);
  return out.length ? out : undefined;
}

export const ChatBox: React.FC<ChatBoxProps> = (props) => {
  const { t } = useTranslation();
  // When inline, accept optional initial state for resuming conversations
  const inlineInitialMessages = props.inline ? props.initialMessages : undefined;
  const inlineInitialConversationId = props.inline ? props.initialConversationId : undefined;
  const onConversationUpdate = props.inline ? props.onConversationUpdate : undefined;
  const onConversationIdAssigned = props.inline ? props.onConversationIdAssigned : undefined;

  const [messages, setMessages] = useState<Message[]>(inlineInitialMessages || []);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingSteps, setStreamingSteps] = useState<StreamingStep[]>([]);
  const [streamingContent, setStreamingContent] = useState('');
  // Server-assigned conversation id; null until the first exchange
  const [conversationId, setConversationId] = useState<string | null>(inlineInitialConversationId ?? null);
  const [starterSuggestions, setStarterSuggestions] = useState<SuggestedQuestionItem[]>([]);
  const [composerPrefill, setComposerPrefill] = useState<{ key: number; text: string } | null>(null);

  const fillComposerWithQuestion = useCallback((text: string) => {
    const t = text.trim();
    if (!t) return;
    setComposerPrefill({ key: Date.now(), text: t });
  }, []);

  const clearComposerPrefill = useCallback(() => {
    setComposerPrefill(null);
  }, []);

  const abortRef = useRef<AbortController | null>(null);
  const stepsRef = useRef<StreamingStep[]>([]);
  const stepCounterRef = useRef(0);
  const nextStepId = () => `step_${++stepCounterRef.current}`;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  // Get site from props (different based on mode)
  const site = props.site;
  const isOrchestrator = site?.domain_type === 'orchestrator';

  useEffect(() => {
    if (!site?.site_id) return;
    if (messages.length > 0 || isLoading) return;
    let cancelled = false;
    fetchSuggestedQuestions(site.site_id, site.org_id ?? '')
      .then((data) => {
        if (cancelled) return;
        if (messagesRef.current.length > 0) return;
        setStarterSuggestions(data.suggestions ?? []);
      })
      .catch(() => {
        if (cancelled) return;
        if (messagesRef.current.length > 0) return;
        setStarterSuggestions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [site?.site_id, site?.org_id, messages.length, isLoading]);

  const fireQualityCheck = useCallback((
    siteId: string,
    question: string,
    answer: string,
    orgId: string,
    docs: DocumentRef[],
    convId: string | undefined,
    turn: number | undefined,
    msgId: string,
  ) => {
    if (!convId || !turn) {
      setMessages((prev) =>
        prev.map((m) => (m.id === msgId ? { ...m, qualityScore: undefined } : m)),
      );
      return;
    }
    // LLM-as-judge and human feedback are hidden for orchestrator domains
    // (see AnalyticsPanel.isOrchestrator) because feedback persistence is
    // scoped to the source-owning site, not the orchestrator. Skip the
    // per-message judge LLM call so we don't burn tokens on a score the UI
    // will never surface.
    if (isOrchestrator) {
      setMessages((prev) =>
        prev.map((m) => (m.id === msgId ? { ...m, qualityScore: undefined } : m)),
      );
      return;
    }
    checkAnswerQuality(siteId, question, answer, orgId, docs, convId, turn)
      .then((quality) => {
        if (!isMountedRef.current) return;
        setMessages((prev) =>
          prev.map((m) => (m.id === msgId ? { ...m, qualityScore: quality || undefined } : m)),
        );
      })
      .catch(() => {
        if (!isMountedRef.current) return;
        setMessages((prev) =>
          prev.map((m) => (m.id === msgId ? { ...m, qualityScore: undefined } : m)),
        );
      });
  }, [isOrchestrator]);

  const handleSendMessage = useCallback(async (content: string) => {
    if (!site) return;
    setStarterSuggestions([]);

    const userMessage: Message = {
      id: generateId(),
      role: 'user',
      content,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    setStreamingSteps([]);
    stepsRef.current = [];
    setStreamingContent('');

    const convOpts = conversationId
      ? { conversation_id: conversationId }
      : { new_conversation: true };

    if (STREAMING_ENABLED) {
      // ---- Streaming path (SSE) ----
      const msgId = generateId();
      let accumulatedAnswer = '';
      let completeFired = false;

      const pushStep = (label: string, details?: Record<string, any>) => {
        const step: StreamingStep = { id: nextStepId(), label, details };
        stepsRef.current = [...stepsRef.current, step];
        setStreamingSteps(stepsRef.current);
      };

      const controller = askQuestionStream(
        site.site_id,
        content,
        site.org_id,
        convOpts,
        (eventType: StreamEventType, data: Record<string, any>) => {
          switch (eventType) {
            case 'phase': {
              pushStep(t(`chat.phase.${data.phase}`, { defaultValue: data.phase }));
              break;
            }
            case 'query_analysis': {
              const entities: string[] = data.entities || [];
              if (entities.length > 0) {
                pushStep(
                  t('chat.stream.foundEntities', { entities: entities.join(', ') }),
                  { rewritten_query: data.rewritten_query, entities, filter_metadata: data.filter_metadata },
                );
              }
              break;
            }
            case 'first_pass': {
              pushStep(t('chat.stream.firstPass', { nodes: data.nodes_found, searches: data.templates_executed }), data);
              break;
            }
            case 'decision': {
              const actionLabel = t(`chat.decision.${data.action}`, { defaultValue: data.action });
              pushStep(actionLabel, { reasoning: data.reasoning, iteration: data.iteration });
              break;
            }
            case 'iteration': {
              pushStep(t('chat.stream.iteration', { iteration: data.iteration, nodes: data.nodes_found }), data);
              break;
            }
            case 'node_enrichment': {
              pushStep(t('chat.stream.nodeEnrichment', { unique: data.unique_nodes, total: data.total_nodes }), { top_titles: data.top_titles });
              break;
            }
            case 'planning_analysis': {
              const subCount = data.sub_query_count || 0;
              const intent = data.intent || 'documentation_query';
              if (data.is_composite && subCount > 1) {
                pushStep(t('chat.stream.decomposed', { count: subCount, intent }), data);
              } else {
                pushStep(t('chat.stream.queryAnalyzed', { intent }), data);
              }
              break;
            }
            case 'planning_group_start': {
              const ids: string[] = data.sub_queries || [];
              pushStep(t('chat.stream.runningSubQueries', { ids: ids.join(', ') }), data);
              break;
            }
            case 'planning_sub_query_complete': {
              const sqId = data.sub_query_id || '?';
              const answered = data.answered !== false;
              const nodesFound = data.nodes_found || 0;
              pushStep(
                t('chat.stream.subQueryComplete', {
                  id: sqId,
                  nodes: nodesFound,
                  noAnswer: answered ? '' : t('chat.stream.noAnswerSuffix'),
                }),
                data,
              );
              break;
            }
            case 'source_routing': {
              const names: string[] = data.source_names || [];
              const count = data.source_count || names.length;
              pushStep(
                count > 0
                  ? t('chat.stream.routingToSources', { count, names: names.join(', ') })
                  : t('chat.stream.routingToSourcesGeneric'),
                data,
              );
              break;
            }
            case 'source_start': {
              const sourceName = data.site_name || data.site_id || t('chat.stream.sourceFallback');
              pushStep(t('chat.stream.searchingSource', { source: sourceName }), data);
              break;
            }
            case 'source_complete': {
              const sourceName = data.site_name || data.site_id || t('chat.stream.sourceFallback');
              const nodeCount = data.node_count ?? 0;
              const elapsed = data.duration_ms;
              const timing = elapsed != null ? t('chat.stream.timing', { seconds: (elapsed / 1000).toFixed(1) }) : '';
              pushStep(t('chat.stream.sourceComplete', { source: sourceName, count: nodeCount, timing }), data);
              break;
            }
            case 'unified_context_ready': {
              const nodeCount = data.node_count ?? 0;
              const srcCount = data.source_count ?? 0;
              pushStep(
                t('chat.stream.collected', {
                  topics: t('chat.stream.collectedTopics', { count: nodeCount }),
                  sources: t('chat.stream.collectedSources', { count: srcCount }),
                }),
                data,
              );
              break;
            }
            case 'answer_chunk': {
              accumulatedAnswer += data.token || '';
              setStreamingContent(accumulatedAnswer);
              break;
            }
            case 'complete': {
              completeFired = true;
              const finalAnswer = data.answer || accumulatedAnswer || t('chat.answer.fallback');
              const finalDocs: DocumentRef[] = (data.final_document_list || []).map((d: Partial<DocumentRef>) => ({
                title: d.title || '',
                guid: d.guid || '',
                relevance_score: d.relevance_score || 0,
                published_url: d.published_url || '',
                source_site_id: d.source_site_id ?? null,
              }));
              const responseConversationId = data.conversation_id;
              const responseTurnNumber = data.turn_number;

              if (responseConversationId) {
                setConversationId(responseConversationId);
                // Only when newly learned (a brand-new chat's first reply) —
                // not on every subsequent turn of a known conversation.
                if (responseConversationId !== conversationId) {
                  onConversationIdAssigned?.(responseConversationId);
                }
              }

              setMessages((prev) => [...prev, {
                id: msgId,
                role: 'assistant' as const,
                content: finalAnswer,
                timestamp: new Date(),
                qualityScore: (isOrchestrator || data.answered === false) ? undefined : null,
                conversationId: responseConversationId || undefined,
                turnNumber: responseTurnNumber || undefined,
                finalDocumentList: finalDocs,
                answered: data.answered !== false,
                clarification: data.clarification === true,
                clarificationOptions: normalizeClarificationOptions(data.clarification_options),
                suggestedAlternativeQuestions: normalizeSuggestedQuestions(
                  data.suggested_alternative_questions,
                ),
                analysisSteps: stepsRef.current.length > 0 ? [...stepsRef.current] : undefined,
                answerReasoning: resolveAnswerReasoning(data, t),
              }]);
              setIsLoading(false);
              setStreamingSteps([]);
              setStreamingContent('');

              if (data.answered !== false) {
                fireQualityCheck(site.site_id, content, finalAnswer, site.org_id, finalDocs, responseConversationId, responseTurnNumber, msgId);
              }
              if (onConversationUpdate) onConversationUpdate();
              break;
            }
            case 'error': {
              setMessages((prev) => [...prev, {
                id: msgId,
                role: 'assistant' as const,
                content: t('chat.error.withMessage', { message: data.message || t('chat.error.unexpected') }),
                timestamp: new Date(),
                isError: true,
              }]);
              setIsLoading(false);
              setStreamingSteps([]);
              setStreamingContent('');
              break;
            }
            default:
              break;
          }
        },
        (error: Error) => {
          if (!completeFired) {
            setMessages((prev) => [...prev, {
              id: msgId,
              role: 'assistant' as const,
              content: t('chat.error.withMessage', { message: getErrorMessage(error, t('chat.error.unexpectedRetry')) }),
              timestamp: new Date(),
              isError: true,
            }]);
            setIsLoading(false);
            setStreamingSteps([]);
            setStreamingContent('');
          }
        },
        () => {
          if (!completeFired) {
            setIsLoading(false);
            setStreamingSteps([]);
            setStreamingContent('');
          }
        },
      );

      abortRef.current = controller;

    } else {
      // ---- Non-streaming path (standard request/response) ----
      try {
        const response = await askQuestion(site.site_id, content, site.org_id, convOpts);

        if (response.conversation_id) {
          setConversationId(response.conversation_id);
          // Only when newly learned (a brand-new chat's first reply) —
          // not on every subsequent turn of a known conversation.
          if (response.conversation_id !== conversationId) {
            onConversationIdAssigned?.(response.conversation_id);
          }
        }

        const msgId = generateId();
        const assistantMessage: Message = {
          id: msgId,
          role: 'assistant',
          content: response.answer || t('chat.answer.fallback'),
          timestamp: new Date(),
          qualityScore: (isOrchestrator || response.answered === false) ? undefined : null,
          conversationId: response.conversation_id || undefined,
          turnNumber: response.turn_number || undefined,
          finalDocumentList: response.final_document_list,
          answered: response.answered !== false,
          clarification: response.clarification === true,
          clarificationOptions: normalizeClarificationOptions(response.clarification_options),
          suggestedAlternativeQuestions: normalizeSuggestedQuestions(
            response.suggested_alternative_questions,
          ),
          answerReasoning: resolveAnswerReasoning(response, t),
        };
        setMessages((prev) => [...prev, assistantMessage]);

        if (response.answered !== false) {
          fireQualityCheck(
            site.site_id, content, response.answer, site.org_id,
            response.final_document_list, response.conversation_id || undefined,
            response.turn_number || undefined, msgId,
          );
        }

        if (onConversationUpdate) onConversationUpdate();
      } catch (error) {
        setMessages((prev) => [...prev, {
          id: generateId(),
          role: 'assistant' as const,
          content: t('chat.error.withMessage', { message: getErrorMessage(error, t('chat.error.unexpectedRetry')) }),
          timestamp: new Date(),
          isError: true,
        }]);
      } finally {
        setIsLoading(false);
      }
    }
  }, [site, conversationId, onConversationUpdate, onConversationIdAssigned, fireQualityCheck, isOrchestrator, t]);

  const handleUserFeedback = useCallback(async (messageId: string, feedback: UserFeedbackSubmission) => {
    if (isOrchestrator) return;

    const msg = messagesRef.current.find((m) => m.id === messageId);
    if (!msg || !msg.conversationId || !msg.turnNumber || !site) return;

    // Optimistically mark the feedback on the message
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, userFeedback: feedback } : m)),
    );

    try {
      await submitUserFeedback(
        site.site_id,
        site.org_id,
        msg.conversationId,
        msg.turnNumber,
        msg.finalDocumentList || [],
        feedback,
      );
    } catch {
      // Revert on failure
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, userFeedback: undefined } : m)),
      );
    }
  }, [site, isOrchestrator]);

  // Clear messages and reset conversation when site *actually changes*
  // within the same component instance.  We track the previous site_id
  // with a ref so the effect never fires on mount (including React 18
  // Strict-Mode double-invocation), preserving initialMessages when
  // resuming a past conversation.
  const prevSiteId = useRef(site?.site_id);
  React.useEffect(() => {
    if (prevSiteId.current !== site?.site_id) {
      setMessages([]);
      setConversationId(null);
      setStarterSuggestions([]);
    }
    prevSiteId.current = site?.site_id;
  }, [site?.site_id]);

  const hasMessages = messages.length > 0 || isLoading;

  const starterChips =
    !hasMessages && starterSuggestions.length > 0 ? (
      <div className="chat-starter-suggestions" aria-label={t('chat.starterSuggestionsLabel')}>
        <div className="chat-starter-suggestions__chips">
          {starterSuggestions.slice(0, 4).map((s, idx) => (
            <button
              key={s.query_id ? `sq-${s.query_id}` : `sq-i-${idx}`}
              type="button"
              className="chat-starter-suggestion-chip"
              title={s.question}
              onClick={() => fillComposerWithQuestion(s.question)}
            >
              <svg className="chat-starter-suggestion-chip__icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <defs>
                  <linearGradient id="chatStarterSparkle" x1="0" y1="16" x2="16" y2="0" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#9A3CF9" />
                    <stop offset="0.4" stopColor="#E743C8" />
                    <stop offset="0.72" stopColor="#ED457E" />
                    <stop offset="1" stopColor="#FF7918" />
                  </linearGradient>
                </defs>
                <path d="M8 0L9.4 5.2L14.9 4.1L10.8 8L14.9 11.9L9.4 10.8L8 16L6.6 10.8L1.1 11.9L5.2 8L1.1 4.1L6.6 5.2L8 0Z" fill="url(#chatStarterSparkle)"/>
              </svg>
              <span className="chat-starter-suggestion-chip__text">{s.question}</span>
            </button>
          ))}
        </div>
      </div>
    ) : null;

  // Inline mode - full page chat view
  if (props.inline) {
    return (
      <div className={InlineChatContainer}>
        {hasMessages ? (
          <>
            <MessageList messages={messages} isLoading={isLoading} siteId={site?.site_id} onSubmitFeedback={isOrchestrator ? undefined : handleUserFeedback} onSendFollowUp={handleSendMessage} onDraftSuggestedQuestion={fillComposerWithQuestion} streamingSteps={streamingSteps} streamingContent={streamingContent} />
            <MessageInput onSend={handleSendMessage} disabled={isLoading} siteDisplayName={site?.site_name} composerPrefill={composerPrefill} onComposerPrefillApplied={clearComposerPrefill} />
          </>
        ) : (
          <div className="chat-empty-stage">
            <div className="chat-empty-stage-inner">
              <p className="chat-inline-greeting">{t('chat.greeting')}</p>
              <MessageInput onSend={handleSendMessage} disabled={isLoading} siteDisplayName={site?.site_name} composerPrefill={composerPrefill} onComposerPrefillApplied={clearComposerPrefill} />
              {starterChips}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Floating panel mode (original behavior)
  const { isOpen, onClose, onOpen } = props as FloatingChatBoxProps;

  // If no site is selected, show disabled button
  if (!site) {
    return (
      <button
        className={ChatButton}
        style={{ opacity: 0.5, cursor: 'not-allowed' }}
        aria-label={t('chat.selectDomainToChat')}
        aria-disabled="true"
      >
        <Chat />
      </button>
    );
  }

  if (!isOpen) {
    return (
      <button
        className={ChatButton}
        onClick={onOpen}
        aria-label={t('chat.chatWith', { siteName: site.site_name })}
      >
        <Chat />
      </button>
    );
  }

  return (
    <div className={ChatPanel}>
      <div className={ChatHeader}>
        <div className={HeaderTitle}>
          <span className={SiteName}>{site.site_name}</span>
          <span className={SiteLabel}>{t('chat.knowledgeGraphQA')}</span>
        </div>
        <button
          className={CloseButton}
          onClick={onClose}
          aria-label={t('chat.closeChat')}
        >
          <Close />
        </button>
      </div>
      <div className="chat-panel-body">
        {hasMessages ? (
          <>
            <MessageList messages={messages} isLoading={isLoading} siteId={site?.site_id} onSubmitFeedback={isOrchestrator ? undefined : handleUserFeedback} onSendFollowUp={handleSendMessage} onDraftSuggestedQuestion={fillComposerWithQuestion} streamingSteps={streamingSteps} streamingContent={streamingContent} />
            <MessageInput onSend={handleSendMessage} disabled={isLoading} siteDisplayName={site.site_name} composerPrefill={composerPrefill} onComposerPrefillApplied={clearComposerPrefill} />
          </>
        ) : (
          <div className="chat-empty-stage">
            <div className="chat-empty-stage-inner">
              <p className="chat-panel-greeting">{t('chat.greeting')}</p>
              <MessageInput onSend={handleSendMessage} disabled={isLoading} siteDisplayName={site.site_name} composerPrefill={composerPrefill} onComposerPrefillApplied={clearComposerPrefill} />
              {starterChips}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatBox;
